import { Link } from "react-router-dom";
import { useRoutines } from "../hooks/useRoutines";
import { useOrgContextKnown } from "../providers/OrgProvider";
import { routineName, routineSetupStatus } from "../lib/routines";
import { PersonalRoutinesNavTree } from "./routines/PersonalRoutinesNavTree";

type RoutinesNavTreeProps = { isCollapsed: boolean; onNavigate: () => void };

/**
 * Mounted only once an organisation is known (see `useOrgContextKnown`). Before that the read
 * is a certain 403, and this tree is not keyed on the org, so it kept saying "Routines
 * unavailable" to a brand-new person after their workspace existed.
 */
export function RoutinesNavTree(props: RoutinesNavTreeProps) {
  return useOrgContextKnown() ? <RoutinesNavTreeForOrg {...props} /> : null;
}

function RoutinesNavTreeForOrg({ isCollapsed, onNavigate }: RoutinesNavTreeProps) {
  const { items, status } = useRoutines("mine", !isCollapsed);
  if (isCollapsed) return null;
  // Story 027.007: saved Routine.v1 routines (same read and IDs as My routines), then the
  // existing listings.
  return (
    <>
      <PersonalRoutinesNavTree onNavigate={onNavigate} />
      <LegacyRoutinesList items={items} status={status} onNavigate={onNavigate} />
    </>
  );
}

function LegacyRoutinesList({
  items,
  status,
  onNavigate,
}: Pick<ReturnType<typeof useRoutines>, "items" | "status"> & { onNavigate: () => void }) {
  if (status === "unavailable" || status === "forbidden" || status === "unauthenticated") {
    return <p className="ml-6 px-3 py-1 text-xs text-muted-foreground">Routines unavailable</p>;
  }
  const unique = [...new Map(items.map((item) => [item.id, item])).values()];
  if (unique.length === 0) return null;
  return (
    <ul
      aria-label="Your routines"
      className="mt-0.5 mb-1 ml-6 space-y-0.5 border-l border-border/60 pl-3"
    >
      {unique.map((item) => (
        <li key={item.id}>
          <Link
            to={`/routines/${encodeURIComponent(item.id)}`}
            onClick={onNavigate}
            className="block rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <span className="block truncate">{routineName(item)}</span>
            <span className="block text-[10px]">{routineSetupStatus(item)}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
